import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Skeleton, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import marketplaceOperatorService, { gateCodesFromError, GateCode, ModerationListing, ModerationStatus } from '../../../services/marketplaceOperatorService';
import { extractErrorMessage } from '../../../services/marketplaceService';
import { formatEuro } from '../../../pages/marketplaceVisibility';
import MaterialSymbol from '../../common/MaterialSymbol';
import ListingPreviewDialog from './ListingPreviewDialog';

const FILTERS: ModerationStatus[] = ['in_review', 'rejected', 'published', 'all'];
const fmt = (iso: string | null): string => (iso ? new Date(iso).toLocaleDateString('de-DE') : '–');

/** Günstigster Einstiegspreis über die angebotenen Lizenzarten ("Kostenlos", wenn keiner > 0 ist). */
export const startingPrice = (l: Pick<ModerationListing, 'allowReference' | 'allowCopy' | 'allowSeatCopy' | 'priceReference' | 'priceCopy' | 'priceSeat'>): number | null => {
    const prices = [
        l.allowReference ? Number(l.priceReference ?? 0) : null,
        l.allowCopy ? Number(l.priceCopy ?? 0) : null,
        l.allowSeatCopy ? Number(l.priceSeat ?? 0) : null,
    ].filter((v): v is number => v !== null);
    return prices.length ? Math.min(...prices) : null;
};

/**
 * Moderations-Queue (T9, Plan E6): öffentliche Angebote in Prüfung mit Vorschau, Freigabe und
 * Ablehnung (Begründung Pflicht). Das Freigabe-Gate (Rechnungsstammdaten + PayPal des Anbieters)
 * ist serverseitig hart; hier erscheint es vorab als Hinweis und bei Ablehnung des Servers (422).
 */
const ModerationQueuePanel: React.FC = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const [status, setStatus] = useState<ModerationStatus>('in_review');
    const [listings, setListings] = useState<ModerationListing[]>([]);
    const [pending, setPending] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [busyId, setBusyId] = useState<number | null>(null);
    const [gate, setGate] = useState<Record<number, GateCode[]>>({});
    const [previewId, setPreviewId] = useState<number | null>(null);
    const [rejecting, setRejecting] = useState<ModerationListing | null>(null);
    const [reason, setReason] = useState('');
    const [reasonError, setReasonError] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await marketplaceOperatorService.getModerationQueue(status);
            setListings(data.listings);
            setPending(data.pending);
        } catch (err) {
            setError(extractErrorMessage(err, translate('marketplaceOperator.common.loadError')));
        } finally {
            setLoading(false);
        }
    }, [status, translate]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    const approve = async (listing: ModerationListing) => {
        setBusyId(listing.id);
        try {
            await marketplaceOperatorService.approve(listing.id);
            notify(translate('marketplaceOperator.moderation.approved'), { type: 'success' });
            setGate((current) => {
                const next = { ...current };
                delete next[listing.id];
                return next;
            });
            await load();
        } catch (err) {
            const codes = gateCodesFromError(err);
            if (codes.length) {
                setGate((current) => ({ ...current, [listing.id]: codes }));
            } else {
                notify(extractErrorMessage(err, translate('marketplaceOperator.common.actionError')), { type: 'error' });
            }
        } finally {
            setBusyId(null);
        }
    };

    const confirmReject = async () => {
        if (!rejecting) return;
        if (reason.trim() === '') {
            setReasonError(true);
            return;
        }
        setBusyId(rejecting.id);
        try {
            await marketplaceOperatorService.reject(rejecting.id, reason.trim());
            notify(translate('marketplaceOperator.moderation.rejected'), { type: 'success' });
            setRejecting(null);
            setReason('');
            await load();
        } catch (err) {
            notify(extractErrorMessage(err, translate('marketplaceOperator.common.actionError')), { type: 'error' });
        } finally {
            setBusyId(null);
        }
    };

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 0.5 }}>{translate('marketplaceOperator.moderation.title')}</Typography>
            <Typography variant="body2" sx={{ color: 'var(--oa-text-2)', mb: 2, maxWidth: 720 }}>{translate('marketplaceOperator.moderation.intro')}</Typography>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={status}
                    onChange={(_, v) => v && setStatus(v)}
                    aria-label={translate('marketplaceOperator.moderation.title')}
                >
                    {FILTERS.map((f) => (
                        <ToggleButton key={f} value={f} data-testid={`filter-${f}`}>{translate(`marketplaceOperator.moderation.filter.${f}`)}</ToggleButton>
                    ))}
                </ToggleButtonGroup>
                {pending > 0 && <Chip color="primary" size="small" label={translate('marketplaceOperator.moderation.pending', { count: pending })} data-testid="pending-count" />}
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading ? (
                <Skeleton variant="rectangular" height={180} />
            ) : listings.length === 0 && !error ? (
                <Alert severity="info">{translate('marketplaceOperator.moderation.empty')}</Alert>
            ) : (
                <Box sx={{ display: 'grid', gap: 2 }}>
                    {listings.map((listing) => {
                        const missing = Array.from(new Set([...(listing.gate ?? []), ...(gate[listing.id] ?? [])]));
                        const price = startingPrice(listing);
                        return (
                            <Paper key={listing.id} variant="outlined" sx={{ p: 2 }} data-testid={`moderation-${listing.id}`}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                                    <Box sx={{ minWidth: 0, flex: '1 1 280px' }}>
                                        <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>{listing.title}</Typography>
                                        <Typography variant="body2" sx={{ color: 'var(--oa-text-2)' }}>
                                            {listing.sellerName ?? translate('marketplaceOperator.common.companyFallback', { id: listing.companyId })}
                                            {' · '}
                                            {price === null || price <= 0 ? translate('marketplaceOperator.moderation.free') : formatEuro(price, listing.currency)}
                                            {' · '}
                                            {translate('marketplaceOperator.moderation.submitted', { date: fmt(listing.submittedAt) })}
                                        </Typography>
                                        {listing.status !== 'in_review' && (
                                            <Chip size="small" variant="outlined" sx={{ mt: 1 }} label={translate(`marketplaceOperator.moderation.filter.${listing.status}`, { _: listing.status })} />
                                        )}
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                        <Button size="small" variant="outlined" startIcon={<MaterialSymbol name="visibility" size={18} />} onClick={() => setPreviewId(listing.id)}>
                                            {translate('marketplaceOperator.moderation.preview')}
                                        </Button>
                                        {listing.status === 'in_review' && (
                                            <>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    startIcon={<MaterialSymbol name="check_circle" size={18} />}
                                                    disabled={busyId === listing.id}
                                                    onClick={() => approve(listing)}
                                                >
                                                    {translate('marketplaceOperator.moderation.approve')}
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="error"
                                                    startIcon={<MaterialSymbol name="cancel" size={18} />}
                                                    disabled={busyId === listing.id}
                                                    onClick={() => { setRejecting(listing); setReason(''); setReasonError(false); }}
                                                >
                                                    {translate('marketplaceOperator.moderation.reject')}
                                                </Button>
                                            </>
                                        )}
                                    </Box>
                                </Box>
                                {listing.status === 'in_review' && missing.length > 0 && (
                                    <Alert severity="warning" sx={{ mt: 1.5 }} data-testid={`gate-${listing.id}`}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{translate('marketplaceOperator.moderation.gateTitle')}</Typography>
                                        <Typography variant="body2">{translate('marketplaceOperator.moderation.gateIntro')}</Typography>
                                        <Box component="ul" sx={{ m: 0, pl: 3 }}>
                                            {missing.map((code) => <li key={code}><Typography variant="body2">{translate(`marketplaceOperator.moderation.gate.${code}`)}</Typography></li>)}
                                        </Box>
                                    </Alert>
                                )}
                                {listing.status === 'rejected' && listing.rejectionReason && (
                                    <Typography variant="body2" sx={{ mt: 1, color: 'var(--oa-text-2)' }}>
                                        {translate('marketplaceOperator.moderation.fields.rejectionReason')}: {listing.rejectionReason}
                                    </Typography>
                                )}
                            </Paper>
                        );
                    })}
                </Box>
            )}

            <ListingPreviewDialog listingId={previewId} onClose={() => setPreviewId(null)} />

            <Dialog open={!!rejecting} onClose={() => setRejecting(null)} fullWidth maxWidth="sm" aria-labelledby="reject-title">
                <DialogTitle id="reject-title">{translate('marketplaceOperator.moderation.rejectTitle')}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="body2" sx={{ color: 'var(--oa-text-2)' }}>
                        {rejecting?.title}. {translate('marketplaceOperator.moderation.rejectIntro')}
                    </Typography>
                    <TextField
                        autoFocus
                        required
                        multiline
                        minRows={3}
                        label={translate('marketplaceOperator.moderation.rejectReason')}
                        value={reason}
                        onChange={(e) => { setReason(e.target.value); setReasonError(false); }}
                        error={reasonError}
                        helperText={reasonError ? translate('marketplaceOperator.moderation.rejectRequired') : undefined}
                        slotProps={{ htmlInput: { maxLength: 2000 } }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setRejecting(null)}>{translate('marketplaceOperator.common.cancel')}</Button>
                    <Button variant="contained" color="error" onClick={confirmReject} disabled={busyId !== null}>
                        {translate('marketplaceOperator.moderation.rejectConfirm')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ModerationQueuePanel;
